import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import moment from 'moment/moment';
import classnames from "classnames";
import {useUsersActionsUIContext} from '../UsersActionsUIContext';
import FilterSelect from '../../../_common/components/FilterSelect';
import FilterText from '../../../_common/components/FilterText';
import {FilterRangePicker} from '../../../_common/components/FieldDatepicker';
import ButtonAction from '../../../_common/components/ButtonAction';
import ClearIcon from '../../../_common/components/ClearIcon';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {getUsersActionsListForExcel} from '../../actions/UsersActionsTableActions';
import {generateExcelFile} from '../../../_common/utils/excel';
import {columnNamesUsersActions} from '../../../_common/constants/excelColumnNames';
import styles from '../../../_common/styles/filtersBar.module.scss';

export function UsersActionsFilters({users}) {
  const usersActionsUIContext = useUsersActionsUIContext();
  const usersActionsUIProps = useMemo(() => {
    return {
      queryParams: usersActionsUIContext.queryParams,
      setQueryParams: usersActionsUIContext.setQueryParams,
    };
  }, [usersActionsUIContext]);
  const {
    actionsOptions,
    sourcesOptions,
    statusesOptions
  } = usersActionsUIContext;
  const [loading, setLoading] = useState(false);

  const intl = useIntl();
  const dispatch = useDispatch();
  const prefix = 'filter-placeholder';

  const qs = new URLSearchParams(window.location.search);
  let responsibleUserID = qs.get('responsibleUserID') || '';
  let page = qs.get('page') || '';
  let subject = qs.get('subject') || '';
  let actionID = qs.get('actionID') || '';
  let statusID = qs.get('statusID') || '';
  let sourceID = qs.get('sourceID') || '';
  let createdFromDate = qs.get('createdFromDate') || '';
  let createdToDate = qs.get('createdToDate') || '';

  const handleExport = () => {
    setLoading(true);
    dispatch(getUsersActionsListForExcel(usersActionsUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(
          data,
          'Users_actions.xlsx',
          'פעולות משתמשים'
        );
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
      }).finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        responsibleUserFirstName,
        responsibleUserLastName,
        createTime,
        sourceName,
        actionName,
        page,
        subject,
        payload,
        statusName
      } = item;

      const username = [responsibleUserFirstName, responsibleUserLastName].filter(Boolean).join(' ');

      return {
        [columnNamesUsersActions[0]]: index + 1,
        [columnNamesUsersActions[1]]: username || '',
        [columnNamesUsersActions[2]]: createTime ? moment.utc(createTime).local().format('DD.MM.YYYY - HH:mm:ss') : '',
        [columnNamesUsersActions[3]]: sourceName || '',
        [columnNamesUsersActions[4]]: actionName || '',
        [columnNamesUsersActions[5]]: page || '',
        [columnNamesUsersActions[6]]: subject || '',
        [columnNamesUsersActions[7]]: payload || '',
        [columnNamesUsersActions[8]]: statusName || '',
      };
    });
  };

  return (
    <Formik
      initialValues={{
        responsibleUserID,
        page,
        subject,
        actionID,
        statusID,
        sourceID,
        createdFromDate,
        createdToDate,
      }}
      onSubmit={(values) => applyFilter(values, usersActionsUIProps)}
      enableReinitialize={true}
    >
      {({values, handleBlur, handleSubmit, setFieldValue}) => (
        <form onSubmit={handleSubmit} className={styles.container}>
          <div className={styles.filtersContainer}>
            <FilterSelect
              fieldName={'responsibleUserID'}
              valueName={values.responsibleUserID}
              options={users}
              placeholder={intl.formatMessage({id: `${prefix}-user`, defaultMessage: 'User'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'page'}
              value={values.page}
              placeholder={intl.formatMessage({id: `${prefix}-page`, defaultMessage: 'Page'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'subject'}
              value={values.subject}
              placeholder={intl.formatMessage({id: `${prefix}-subject`, defaultMessage: 'Subject'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'actionID'}
              valueName={values.actionID}
              options={actionsOptions}
              placeholder={intl.formatMessage({id: `${prefix}-action`, defaultMessage: 'Action'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'statusID'}
              valueName={values.statusID}
              options={statusesOptions}
              placeholder={intl.formatMessage({id: `${prefix}-status`, defaultMessage: 'Status'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'sourceID'}
              valueName={values.sourceID}
              options={sourcesOptions}
              placeholder={intl.formatMessage({id: `${prefix}-source`, defaultMessage: 'Source'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <div className={classnames(styles.filter, 'w-350px')}>
              <FilterRangePicker
                onChange={(fromDate, toDate) => {
                  setFieldValue('createdFromDate', fromDate || '');
                  setFieldValue('createdToDate', toDate || '');
                  handleSubmit();
                }}
                placeholder={[intl.formatMessage({id: `${prefix}-create-date-from-to`, defaultMessage: 'Create date'})]}
                startDate={createdFromDate}
                endDate={createdToDate}
              />
              <ClearIcon
                onClick={() => {
                  setFieldValue('createdFromDate', '');
                  setFieldValue('createdToDate', '');
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <ButtonAction
              variant={'success'}
              label={
                <>
                  {loading
                    ? (
                      <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                    )
                    : (
                      <>
                        <img
                          src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                          alt="export" className={'mr-2'}
                        />
                        <span>{intl.formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                      </>
                    )
                  }
                </>
              }
              onClick={handleExport}
              customClassnames={'w-100px'}
            />
          </div>
        </form>
      )}
    </Formik>
  );
}

export default UsersActionsFilters;
